import { forwardRef, useEffect, useId, useRef, useState } from 'react';
import type { WithClassName } from './types';

export type IdTextProps = WithClassName & {
  id: string | null;
  name?: string | null;
  entity: 'user' | 'role' | 'connection' | 'request' | 'asset' | 'record';
  href?: string;
  missingLabel?: string;
  copy?: boolean;
};

export const IdText = forwardRef<HTMLSpanElement, IdTextProps>(function IdText(
  { id, name, entity, href, missingLabel, copy = true, className }, ref,
) {
  const [state, setState] = useState<'idle' | 'copying' | 'copied' | 'denied'>('idle');
  const [open, setOpen] = useState(false);
  const diagnosticId = useId();
  const pending = useRef(false);
  const version = useRef(0);
  const disclosure = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    version.current++;
    pending.current = false;
    setState('idle');
    setOpen(false);
    return () => { version.current++; };
  }, [id]);
  const label = name?.trim() || missingLabel
    || (entity === 'connection' && id ? 'Unknown connection (deleted)' : `Unknown ${entity}`);
  async function copyId() {
    if (!id || pending.current) return;
    pending.current = true;
    const current = version.current;
    setState('copying');
    try {
      await navigator.clipboard.writeText(id);
      if (current === version.current) setState('copied');
    } catch {
      if (current === version.current) {
        setState('denied');
        setOpen(true);
        // The disclosure remains selectable and reachable even without Clipboard API permission.
        disclosure.current?.focus();
      }
    } finally {
      if (current === version.current) pending.current = false;
    }
  }
  return <span ref={ref} className={`ui-id-text ${className ?? ''}`}>
    {href && id ? <a href={href}>{label}</a> : <span>{label}</span>}
    {id && <>
      {copy && <button type="button" className="ui-button ui-button--small" disabled={state === 'copying'}
        aria-label={`Copy full ID for ${label}`} onClick={() => { void copyId(); }}>
        {state === 'copying' ? 'Copying…' : 'Copy ID'}
      </button>}
      <span className="ui-id-text__details">
        <button ref={disclosure} type="button" className="ui-button ui-button--small" aria-expanded={open}
          aria-controls={diagnosticId} onClick={() => setOpen(!open)}>Diagnostic ID for {label}</button>
        <code id={diagnosticId} hidden={!open} tabIndex={0}
          onFocus={(event) => {
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(event.currentTarget);
            selection?.removeAllRanges();
            selection?.addRange(range);
          }}>{id}</code>
      </span>
    </>}
    <span role="status">{state === 'copied' ? 'Full ID copied' : state === 'denied'
      ? 'Copy unavailable — select the full ID' : ''}</span>
  </span>;
});

export const EntityRef = IdText;
